import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, User, Lock, Eye, EyeOff, Search, RefreshCw, KeyRound, Database, FileText } from 'lucide-react';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://nocodb.aibrick.co.kr';
const XC_TOKEN = import.meta.env.VITE_XC_TOKEN || 'FIxhc3kiKgR7TVnzUX8LyXARQSdTjJt0U5AcrOff'; // NocoDB에서 발급받은 API 토큰을 기입해 주세요.

type ConsultationStatus = '신청' | '취소' | '완료';

interface Consultation {
  id: number;
  name: string;
  phone: string;
  amount: string;
  product: string;
  created_at?: string;
  status: ConsultationStatus;
}

// ── 상태 셀렉트 컴포넌트 (독립 로컬 state로 즉시 반영) ──────────────
function StatusSelect({
  recordId,
  initialStatus,
  onSave,
}: {
  recordId: number;
  initialStatus: ConsultationStatus;
  onSave: (id: number, status: ConsultationStatus) => void;
}) {
  const [status, setStatus] = useState<ConsultationStatus>(initialStatus);

  const colorClass =
    status === '완료'
      ? 'bg-emerald-900/40 text-emerald-400 border-emerald-600/40'
      : status === '취소'
      ? 'bg-red-900/30 text-red-400 border-red-600/40'
      : 'bg-blue-900/30 text-blue-400 border-blue-600/40';

  return (
    <select
      value={status}
      onChange={(e) => {
        const next = e.target.value as ConsultationStatus;
        setStatus(next);        // 즉시 UI 반영
        onSave(recordId, next); // 서버 저장 (비동기)
      }}
      className={`text-xs font-semibold px-3 py-1.5 rounded-lg border cursor-pointer outline-none transition-all ${colorClass}`}
    >
      <option value="신청">신청</option>
      <option value="취소">취소</option>
      <option value="완료">완료</option>
    </select>
  );
}


interface AdminUser {
  id: number;
  username: string;
}

export default function AdminPage() {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [activeTab, setActiveTab] = useState<'submissions' | 'profile'>('submissions');
  
  // Login Form States
  const [loginUsername, setLoginUsername] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  // Data States
  const [submissions, setSubmissions] = useState<Consultation[]>([]);
  const [statusMap, setStatusMap] = useState<Record<number, ConsultationStatus>>({});
  const [adminUser, setAdminUser] = useState<AdminUser | null>(null);
  const [loadingData, setLoadingData] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<ConsultationStatus | 'all'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);
  
  // Profile Update States
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [profileSuccess, setProfileSuccess] = useState('');
  const [profileError, setProfileError] = useState('');
  const [profileLoading, setProfileLoading] = useState(false);

  // Check login state on mount
  useEffect(() => {
    const savedUser = localStorage.getItem('toad_admin_user');
    if (savedUser) {
      try {
        const parsed = JSON.parse(savedUser);
        setAdminUser(parsed);
        setIsLoggedIn(true);
        setNewUsername(parsed.username);
      } catch {
        localStorage.removeItem('toad_admin_user');
      }
    }
  }, []);

  // Fetch Consultations
  const fetchSubmissions = useCallback(async () => {
    if (!isLoggedIn) return;
    setLoadingData(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/v3/data/p32e7pagy9rr8w4/mxbg7xtdv44lmyp/records?limit=100`, {
        headers: {
          'xc-token': XC_TOKEN,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        const data = await response.json();
        // NocoDB v3 wraps records in `records`
        const rawList = data.records || [];
        const list: Consultation[] = rawList.map((r: any) => ({
          id: r.id,
          name: r.fields.name,
          phone: r.fields.phone,
          amount: r.fields.amount,
          product: r.fields.product,
          created_at: r.fields.CreatedAt,
          status: (r.fields.status as ConsultationStatus) || '신청'
        }));
        
        // Sort by id desc (newest first)
        list.sort((a, b) => b.id - a.id);
        
        setSubmissions(list);
        // Initialise statusMap from fetched data
        const map: Record<number, ConsultationStatus> = {};
        list.forEach(item => { map[item.id] = item.status; });
        setStatusMap(map);
      } else {
        console.error('Failed to fetch submissions:', response.statusText);
      }
    } catch (err) {
      console.error('Network error fetching submissions:', err);
    } finally {
      setLoadingData(false);
    }
  }, [isLoggedIn]);

  // Update consultation status
  const updateStatus = useCallback(async (id: number, newStatus: ConsultationStatus) => {
    // Immediate local UI update via statusMap
    setStatusMap(prev => ({ ...prev, [id]: newStatus }));
    try {
      const res = await fetch(`${API_BASE_URL}/api/v3/data/p32e7pagy9rr8w4/mxbg7xtdv44lmyp/records`, {
        method: 'PATCH',
        headers: {
          'xc-token': XC_TOKEN,
          'Content-Type': 'application/json'
        },
        // NocoDB v3 PATCH requires an array of records
        body: JSON.stringify([{ id, fields: { status: newStatus } }])
      });
      if (!res.ok) {
        const errText = await res.text();
        console.error('PATCH status failed:', res.status, errText);
      }
    } catch (err) {
      console.error('Failed to update status:', err);
    }
  }, []);

  useEffect(() => {
    if (isLoggedIn) {
      fetchSubmissions();
    }
  }, [isLoggedIn, fetchSubmissions]);

  // Handle Login
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError('');
    setLoginLoading(true);

    if (XC_TOKEN === 'YOUR_NOCODB_XC_TOKEN') {
      setLoginError('NocoDB API 토큰(XC_TOKEN) 설정이 필요합니다. AdminPage.tsx 파일 상단을 확인해 주세요.');
      setLoginLoading(false);
      return;
    }

    try {
      // Query AdminUsers table to verify credentials using NocoDB v3 API
      const response = await fetch(
        `${API_BASE_URL}/api/v3/data/p32e7pagy9rr8w4/mb5zmdm9pdhsfr3/records?where=(username,eq,${encodeURIComponent(loginUsername)})`,
        {
          headers: {
            'xc-token': XC_TOKEN,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        const records = data.records || [];
        
        const users = records.map((r: any) => ({
          id: r.id,
          username: r.fields.username,
          password: r.fields.password
        }));
        
        const matchedUser = users.find(
          (u: any) => u.username === loginUsername && u.password === loginPassword
        );

        if (matchedUser) {
          const userSession = { id: matchedUser.id, username: matchedUser.username };
          localStorage.setItem('toad_admin_user', JSON.stringify(userSession));
          setAdminUser(userSession);
          setNewUsername(matchedUser.username);
          setIsLoggedIn(true);
          setLoginPassword('');
        } else {
          setLoginError('아이디 또는 비밀번호가 올바르지 않습니다.');
        }
      } else {
        setLoginError('인증 서버에 연결할 수 없습니다. (NocoDB 상태 확인 요망)');
      }
    } catch (err) {
      console.error('Login error:', err);
      setLoginError('네트워크 오류가 발생했습니다. NocoDB 주소 및 연결 상태를 확인해 주세요.');
    } finally {
      setLoginLoading(false);
    }
  };

  // Handle Logout
  const handleLogout = () => {
    localStorage.removeItem('toad_admin_user');
    setAdminUser(null);
    setIsLoggedIn(false);
    setActiveTab('submissions');
    setLoginUsername('');
    setLoginPassword('');
  };

  // Update Profile (ID & Password)
  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setProfileError('');
    setProfileSuccess('');
    
    if (!adminUser) return;
    if (!newUsername.trim()) {
      setProfileError('아이디를 입력해 주세요.');
      return;
    }
    if (newPassword && newPassword !== confirmPassword) {
      setProfileError('새 비밀번호가 일치하지 않습니다.');
      return;
    }

    setProfileLoading(true);
    try {
      const fieldsPayload: Record<string, string> = {
        username: newUsername.trim()
      };
      if (newPassword) {
        fieldsPayload.password = newPassword;
      }

      // NocoDB v3 PATCH updates records by specifying the array of records containing id and fields
      const response = await fetch(
        `${API_BASE_URL}/api/v3/data/p32e7pagy9rr8w4/mb5zmdm9pdhsfr3/records`,
        {
          method: 'PATCH',
          headers: {
            'xc-token': XC_TOKEN,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            id: adminUser.id,
            fields: fieldsPayload
          })
        }
      );

      if (response.ok) {
        setProfileSuccess('관리자 정보가 성공적으로 변경되었습니다.');
        const updatedUser = { ...adminUser, username: newUsername.trim() };
        localStorage.setItem('toad_admin_user', JSON.stringify(updatedUser));
        setAdminUser(updatedUser);
        setNewPassword('');
        setConfirmPassword('');
      } else {
        setProfileError('정보 변경에 실패했습니다. 다시 시도해 주세요.');
      }
    } catch (err) {
      console.error('Profile update error:', err);
      setProfileError('서버 통신 중 네트워크 오류가 발생했습니다.');
    } finally {
      setProfileLoading(false);
    }
  };
  // Filter submissions by search query & status filter
  const filteredSubmissions = submissions.filter((item) => {
    const query = searchTerm.toLowerCase();
    const productMap: Record<string, string> = {
      corporate: '법인사업자대출',
      personal: '개인사업자대출',
      self: '자영업자대출',
      enterprise: '기업단기대출',
      daily: '일수대출'
    };
    const productKorean = productMap[item.product] || '';
    
    const matchesStatus = statusFilter === 'all' || item.status === statusFilter;
    const matchesSearch = 
      item.name.toLowerCase().includes(query) ||
      item.phone.includes(query) ||
      item.amount.toLowerCase().includes(query) ||
      item.product.toLowerCase().includes(query) ||
      productKorean.includes(query);

    return matchesStatus && matchesSearch;
  });

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredSubmissions.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredSubmissions.length / itemsPerPage);

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100 font-sans flex flex-col">
      {/* Header */}
      <header className="bg-slate-950 border-b border-slate-800 px-6 py-4 flex items-center justify-between sticky top-0 z-40">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-blue-600 flex items-center justify-center shadow-lg shadow-blue-500/20">
            <Database className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="font-bold text-lg leading-tight tracking-tight">대출 중개 어드민</h1>
            <p className="text-[10px] text-slate-500 font-medium tracking-wider uppercase">Toad Loan Admin Panel</p>
          </div>
        </div>
        
        {isLoggedIn && (
          <div className="flex items-center gap-4">
            <span className="text-xs text-slate-400 bg-slate-800/60 px-3 py-1.5 rounded-full border border-slate-700/40 font-medium">
              접속 계정: <strong className="text-blue-400 font-semibold">{adminUser?.username}</strong>
            </span>
            <button
              onClick={handleLogout}
              className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-red-400 font-semibold transition-colors duration-200"
            >
              <LogOut className="w-4 h-4" />
              로그아웃
            </button>
          </div>
        )}
      </header>

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {!isLoggedIn ? (
          /* Login Panel */
          <div className="flex-1 flex items-center justify-center p-4">
            <div className="w-full max-w-md bg-slate-950 rounded-2xl border border-slate-800 p-8 shadow-2xl relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500" />
              
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold tracking-tight text-white mb-2">관리자 로그인</h2>
                <p className="text-sm text-slate-400">데이터 조회를 위해 인증이 필요합니다.</p>
              </div>

              <form onSubmit={handleLogin} className="space-y-5">
                <div>
                  <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">아이디</label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-500">
                      <User className="w-4 h-4" />
                    </span>
                    <input
                      type="text"
                      required
                      placeholder="Username"
                      value={loginUsername}
                      onChange={(e) => setLoginUsername(e.target.value)}
                      autoComplete="off"
                      className="w-full bg-slate-900 border border-slate-800 rounded-xl py-3 pl-10 pr-4 text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">비밀번호</label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-500">
                      <Lock className="w-4 h-4" />
                    </span>
                    <input
                      type={showPassword ? "text" : "password"}
                      required
                      placeholder="Password"
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      autoComplete="new-password"
                      className="w-full bg-slate-900 border border-slate-800 rounded-xl py-3 pl-10 pr-12 text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-slate-500 hover:text-slate-300 transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {loginError && (
                  <div className="p-3 bg-red-950/50 border border-red-900/60 rounded-xl text-xs text-red-400 font-medium">
                    {loginError}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loginLoading}
                  className="w-full bg-blue-600 hover:bg-blue-500 text-white font-semibold py-3.5 px-4 rounded-xl text-sm transition-all duration-300 shadow-lg shadow-blue-500/25 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loginLoading ? '로그인 처리 중...' : '로그인'}
                </button>
              </form>
            </div>
          </div>
        ) : (
          /* Dashboard Panel */
          <div className="flex-1 flex flex-col lg:flex-row">
            {/* Sidebar */}
            <aside className="w-full lg:w-64 bg-slate-950/80 border-r border-slate-800/80 p-4 flex flex-row lg:flex-col gap-2">
              <button
                onClick={() => setActiveTab('submissions')}
                className={`flex-1 lg:flex-initial flex items-center gap-2.5 px-4 py-3 rounded-xl text-sm font-semibold transition-all ${
                  activeTab === 'submissions'
                    ? 'bg-blue-600/10 text-blue-400 border border-blue-500/20'
                    : 'text-slate-400 hover:bg-slate-900 hover:text-slate-200 border border-transparent'
                }`}
              >
                <FileText className="w-4 h-4" />
                법인사업자 신청내역
              </button>
              <button
                onClick={() => setActiveTab('profile')}
                className={`flex-1 lg:flex-initial flex items-center gap-2.5 px-4 py-3 rounded-xl text-sm font-semibold transition-all ${
                  activeTab === 'profile'
                    ? 'bg-blue-600/10 text-blue-400 border border-blue-500/20'
                    : 'text-slate-400 hover:bg-slate-900 hover:text-slate-200 border border-transparent'
                }`}
              >
                <KeyRound className="w-4 h-4" />
                관리자 계정 변경
              </button>
            </aside>

            {/* Content Area */}
            <main className="flex-1 p-6 lg:p-8">
              {activeTab === 'submissions' ? (
                /* Submissions List View */
                <div className="space-y-6">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                      <h2 className="text-xl font-bold text-white">전체 대출 상담 신청 목록</h2>
                      <p className="text-xs text-slate-400 mt-1">접수 완료된 모든 대출 상담 신청 목록입니다.</p>
                    </div>
                    <button
                      onClick={fetchSubmissions}
                      disabled={loadingData}
                      className="inline-flex items-center justify-center gap-1.5 bg-slate-850 hover:bg-slate-800 border border-slate-700/80 text-xs font-semibold px-4 py-2.5 rounded-xl text-slate-300 hover:text-white transition-all disabled:opacity-60"
                    >
                      <RefreshCw className={`w-3.5 h-3.5 ${loadingData ? 'animate-spin' : ''}`} />
                      목록 새로고침
                    </button>
                  </div>

                  {/* Search & Filter Bar */}
                  <div className="flex flex-col sm:flex-row gap-3">
                    <div className="relative flex-1 max-w-md">
                      <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-500">
                        <Search className="w-4 h-4" />
                      </span>
                      <input
                        type="text"
                        placeholder="이름, 연락처, 상품, 신청금액 검색..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full bg-slate-950 border border-slate-800 rounded-xl py-2.5 pl-10 pr-4 text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
                      />
                    </div>
                    <div className="w-full sm:w-40">
                      <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value as ConsultationStatus | 'all')}
                        className="w-full bg-slate-950 border border-slate-800 rounded-xl py-2.5 px-4 text-sm text-slate-300 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all cursor-pointer font-medium"
                      >
                        <option value="all">전체 상태</option>
                        <option value="신청">신청</option>
                        <option value="취소">취소</option>
                        <option value="완료">완료</option>
                      </select>
                    </div>
                  </div>

                  {/* Table View */}
                  <div className="bg-slate-950 rounded-2xl border border-slate-800 overflow-hidden shadow-xl">
                    {loadingData && submissions.length === 0 ? (
                      <div className="py-20 flex flex-col items-center justify-center text-slate-400 gap-3">
                        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
                        <span className="text-sm font-medium">데이터를 로드하고 있습니다...</span>
                      </div>
                    ) : filteredSubmissions.length === 0 ? (
                      <div className="py-20 flex flex-col items-center justify-center text-slate-500 gap-2">
                        <FileText className="w-10 h-10 text-slate-700 mb-1" />
                        <span className="text-sm font-medium">접수된 상담 신청이 없습니다.</span>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-center border-collapse">
                          <thead>
                            <tr className="border-b border-slate-800 bg-slate-900/50 text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                              <th className="py-4 px-6 text-center w-16">No.</th>
                              <th className="py-4 px-6 text-center">성함 (대표자명)</th>
                              <th className="py-4 px-6 text-center">연락처</th>
                              <th className="py-4 px-6 text-center">신청 상품</th>
                              <th className="py-4 px-6 text-center">신청 금액</th>
                              <th className="py-4 px-6 text-center">상태</th>
                              <th className="py-4 px-6 text-center">신청 일시</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-800/60 text-sm text-slate-300">
                            {currentItems.map((item, idx) => (
                              <tr key={item.id} className="hover:bg-slate-900/30 transition-colors">
                                <td className="py-4 px-6 text-center text-xs font-mono text-slate-500">
                                  {filteredSubmissions.length - (indexOfFirstItem + idx)}
                                </td>
                                <td className="py-4 px-6 text-center font-semibold text-white">
                                  {item.name}
                                </td>
                                <td className="py-4 px-6 text-center font-mono text-slate-300">
                                  {item.phone.replace(/(\d{3})(\d{3,4})(\d{4})/, '$1-$2-$3')}
                                </td>
                                <td className="py-4 px-6 text-center">
                                  <span className="inline-flex items-center justify-center px-2.5 py-1.5 rounded-lg bg-slate-800 text-slate-300 text-xs font-semibold border border-slate-700">
                                    {item.product === 'corporate' && '법인사업자대출'}
                                    {item.product === 'personal' && '개인사업자대출'}
                                    {item.product === 'self' && '자영업자대출'}
                                    {item.product === 'enterprise' && '기업단기대출'}
                                    {item.product === 'daily' && '일수대출'}
                                    {!['corporate', 'personal', 'self', 'enterprise', 'daily'].includes(item.product) && item.product}
                                  </span>
                                </td>
                                <td className="py-4 px-6 text-center">
                                  <span className="inline-flex items-center justify-center px-2.5 py-1.5 rounded-lg bg-blue-900/30 text-blue-400 text-xs font-semibold border border-blue-500/10">
                                    {item.amount === '1000' && '1,000만원 이상'}
                                    {item.amount === '3000' && '3,000만원 이상'}
                                    {item.amount === '5000' && '5,000만원 이상'}
                                    {item.amount === '10000' && '1억원 이상'}
                                    {item.amount === '100000' && '10억원 이상'}
                                    {!['1000', '3000', '5000', '10000', '100000'].includes(item.amount) && item.amount}
                                  </span>
                                </td>
                                <td className="py-4 px-6 text-center">
                                  {(() => {
                                    const s = statusMap[item.id] ?? '신청';
                                    return (
                                      <select
                                        value={s}
                                        onChange={(e) => updateStatus(item.id, e.target.value as ConsultationStatus)}
                                        className={`text-xs font-semibold px-3 py-1.5 rounded-lg border cursor-pointer outline-none transition-all ${
                                          s === '완료'
                                            ? 'bg-emerald-900/40 text-emerald-400 border-emerald-600/40'
                                            : s === '취소'
                                            ? 'bg-red-900/30 text-red-400 border-red-600/40'
                                            : 'bg-blue-900/30 text-blue-400 border-blue-600/40'
                                        }`}
                                      >
                                        <option value="신청">신청</option>
                                        <option value="취소">취소</option>
                                        <option value="완료">완료</option>
                                      </select>
                                    );
                                  })()}
                                </td>
                                <td className="py-4 px-6 text-center text-xs text-slate-400 font-medium">
                                  {item.created_at ? new Date(item.created_at).toLocaleString('ko-KR', {
                                    year: 'numeric',
                                    month: '2-digit',
                                    day: '2-digit',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    second: '2-digit'
                                  }) : '-'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* Pagination Controls */}
                    {filteredSubmissions.length > 0 && (
                      <div className="px-6 py-4 bg-slate-950 border-t border-slate-800/60 flex flex-col sm:flex-row items-center justify-between gap-4">
                        <div className="text-xs text-slate-400">
                          총 <span className="font-semibold text-slate-200">{filteredSubmissions.length}</span>건 중{' '}
                          <span className="font-semibold text-slate-200">{indexOfFirstItem + 1}</span> -{' '}
                          <span className="font-semibold text-slate-200">
                            {Math.min(indexOfLastItem, filteredSubmissions.length)}
                          </span>건 표시
                        </div>
                        
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                            disabled={currentPage === 1}
                            className="px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-400 hover:text-white hover:bg-slate-900 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-400 transition-all border border-slate-800"
                          >
                            이전
                          </button>
                          
                          {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
                            <button
                              key={pageNum}
                              onClick={() => setCurrentPage(pageNum)}
                              className={`w-8 h-8 rounded-lg text-xs font-semibold transition-all ${
                                currentPage === pageNum
                                  ? 'bg-blue-600 text-white'
                                  : 'text-slate-400 hover:text-white hover:bg-slate-900'
                              }`}
                            >
                              {pageNum}
                            </button>
                          ))}

                          <button
                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                            disabled={currentPage === totalPages}
                            className="px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-400 hover:text-white hover:bg-slate-900 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-400 transition-all border border-slate-800"
                          >
                            다음
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                /* Profile Update View */
                <div className="max-w-xl space-y-6">
                  <div>
                    <h2 className="text-xl font-bold text-white">관리자 계정 정보 수정</h2>
                    <p className="text-xs text-slate-400 mt-1">어드민 패널에 로그인할 아이디와 비밀번호를 변경합니다.</p>
                  </div>

                  <div className="bg-slate-950 border border-slate-800 rounded-2xl p-6 shadow-xl">
                    <form onSubmit={handleUpdateProfile} className="space-y-5">
                      <div>
                        <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">관리자 아이디</label>
                        <input
                          type="text"
                          required
                          value={newUsername}
                          onChange={(e) => setNewUsername(e.target.value)}
                          className="w-full bg-slate-900 border border-slate-800 rounded-xl py-3 px-4 text-sm text-slate-200 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                          새 비밀번호 <span className="text-[10px] text-slate-500 font-normal lowercase">(변경하지 않으려면 공란)</span>
                        </label>
                        <input
                          type="password"
                          placeholder="새로운 비밀번호를 입력해 주세요."
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          className="w-full bg-slate-900 border border-slate-800 rounded-xl py-3 px-4 text-sm text-slate-200 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
                        />
                      </div>

                      {newPassword && (
                        <div>
                          <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">새 비밀번호 확인</label>
                          <input
                            type="password"
                            placeholder="새로운 비밀번호를 한 번 더 입력해 주세요."
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            className="w-full bg-slate-900 border border-slate-800 rounded-xl py-3 px-4 text-sm text-slate-200 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
                          />
                        </div>
                      )}

                      {profileError && (
                        <div className="p-3 bg-red-950/50 border border-red-900/60 rounded-xl text-xs text-red-400 font-medium">
                          {profileError}
                        </div>
                      )}

                      {profileSuccess && (
                        <div className="p-3 bg-blue-950/50 border border-blue-900/60 rounded-xl text-xs text-blue-400 font-medium">
                          {profileSuccess}
                        </div>
                      )}

                      <button
                        type="submit"
                        disabled={profileLoading}
                        className="w-full sm:w-auto bg-blue-600 hover:bg-blue-500 text-white font-semibold py-3 px-6 rounded-xl text-sm transition-all duration-300 shadow-lg shadow-blue-500/25 disabled:opacity-60"
                      >
                        {profileLoading ? '저장 중...' : '계정 정보 저장'}
                      </button>
                    </form>
                  </div>
                </div>
              )}
            </main>
          </div>
        )}
      </div>
    </div>
  );
}
